
import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string;
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// ── Inner Payment Form ────────────────────────────────────────────────────────
function PaymentForm({
  itemName,
  price,
  type,
  onSuccess,
}: {
  itemName: string;
  price: number;
  type: string;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!name.trim() || !email.trim()) {
      setErrorMsg('Please fill in your name and email.');
      return;
    }
    setProcessing(true);
    setErrorMsg('');

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/checkout?success=true&item=${encodeURIComponent(itemName)}&price=${price}&type=${encodeURIComponent(type)}`,
        receipt_email: email,
        payment_method_data: {
          billing_details: { name, email, phone },
        },
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMsg(error.message || 'Payment failed. Please try again.');
      setProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          {/* Your Information */}
          <div className="bg-white rounded-2xl shadow-md p-8 border border-amber-100">
            <h2 className="font-display text-xl font-bold text-neutral-800 mb-6 flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-700 text-white text-xs font-bold">1</span>
              Your Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-neutral-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">
                  Due Date <span className="text-neutral-400 font-normal">(optional)</span>
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-neutral-700 mb-1">
                  Additional Notes <span className="text-neutral-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => { if (e.target.value.length <= 500) setNotes(e.target.value); }}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm resize-none"
                  placeholder="Any special requests or notes..."
                />
                <p className="text-xs text-neutral-400 mt-1 text-right">{notes.length}/500</p>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-2xl shadow-md p-8 border border-amber-100">
            <h2 className="font-display text-xl font-bold text-neutral-800 mb-2 flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-700 text-white text-xs font-bold">2</span>
              Payment Method
            </h2>
            <p className="text-xs text-neutral-500 mb-5 ml-9">
              All major cards, Apple Pay, Google Pay, and online banking accepted.
            </p>
            <PaymentElement
              onReady={() => setReady(true)}
              options={{
                layout: { type: 'accordion', defaultCollapsed: false, radios: true, spacedAccordionItems: true },
              }}
            />
          </div>

          {errorMsg && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700 flex items-center gap-2">
                <i className="ri-error-warning-line flex-shrink-0" />
                {errorMsg}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={!stripe || !ready || processing}
            className="w-full py-4 bg-amber-700 text-white font-bold rounded-full hover:bg-amber-800 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-all whitespace-nowrap cursor-pointer text-base shadow-lg"
          >
            {processing ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" /> Processing...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-lock-line" /> Complete Secure Payment — ${price.toLocaleString()}
              </span>
            )}
          </button>

          <p className="text-center text-xs text-neutral-500 pb-4">
            <i className="ri-shield-check-line text-green-600 mr-1" />
            Secured by Stripe. We never store your card details.
          </p>
        </div>

        {/* RIGHT — Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-gradient-to-b from-amber-50 to-green-50 rounded-2xl shadow-md p-8 border-2 border-amber-200 sticky top-6">
            <h2 className="font-display text-lg font-bold text-neutral-800 mb-6">Order Summary</h2>
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                <i className={`${type === 'package' ? 'ri-gift-line' : type === 'alacarte' ? 'ri-service-line' : 'ri-file-text-line'} text-amber-700`} />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-800 text-sm">{itemName}</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {type === 'package' ? 'Doula Package' : type === 'alacarte' ? 'À La Carte Service' : 'Birth Plan'}
                </p>
              </div>
            </div>
            <div className="border-t border-amber-300 pt-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-neutral-700">Total Due</span>
                <span className="font-display text-3xl font-bold text-amber-700">${price.toLocaleString()}</span>
              </div>
            </div>
            <div className="bg-white/60 rounded-xl p-4 text-xs text-neutral-600 space-y-2">
              {[
                '256-bit SSL encryption',
                'Instant email confirmation',
                'Apple Pay & Google Pay accepted',
                'Online banking supported',
                '24/7 customer support',
              ].map((text) => (
                <p key={text} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-green-600 mt-0.5 flex-shrink-0" />
                  <span>{text}</span>
                </p>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-amber-200 text-center">
              <Link to="/packages" className="text-xs text-amber-700 underline hover:text-amber-900 cursor-pointer">
                ← Back to Packages
              </Link>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

// ── Success Screen ────────────────────────────────────────────────────────────
function SuccessScreen({ itemName }: { itemName: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-10 text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-6">
          <i className="ri-checkbox-circle-fill text-green-600 text-4xl" />
        </div>
        <h2 className="font-display text-3xl font-bold text-neutral-800 mb-3">Payment Successful!</h2>
        <p className="text-neutral-600 text-sm mb-8">
          Thank you for your purchase of <strong>{itemName}</strong>. You&apos;ll receive a confirmation email shortly. We&apos;re excited to support you on your journey!
        </p>
        <div className="flex flex-col gap-3">
          <Link
            to="/booking"
            className="inline-block px-8 py-3 bg-amber-700 text-white font-bold rounded-full hover:bg-amber-800 transition-all whitespace-nowrap cursor-pointer"
          >
            Schedule Your First Visit
          </Link>
          <Link to="/" className="text-sm text-amber-700 underline hover:text-amber-900 cursor-pointer">
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Root Checkout Page ────────────────────────────────────────────────────────
export default function CheckoutPage() {
  const [searchParams] = useSearchParams();

  const itemName = useRef(searchParams.get('item') || 'Unknown Item').current;
  const price = useRef(Math.max(0, parseFloat(searchParams.get('price') || '0') || 0)).current;
  const type = useRef(searchParams.get('type') || 'package').current;
  const isSuccess = useRef(
    searchParams.get('success') === 'true' ||
    searchParams.get('payment_intent_client_secret') != null
  ).current;

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise] = useState<Promise<Stripe | null>>(() => loadStripe(STRIPE_KEY));
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(isSuccess);

  const initialized = useRef(false);

  useEffect(() => {
    if (isSuccess || initialized.current) return;
    initialized.current = true;

    if (!price || price <= 0) {
      setError('Invalid price. Please go back and select a package.');
      return;
    }

    fetch(`${SUPABASE_URL}/functions/v1/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        amount: Math.round(price * 100),
        currency: 'usd',
        packageName: itemName,
        productType: type,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          setError(data.error || 'Could not create payment session. Please try again.');
        }
      })
      .catch(() => {
        setError('Network error. Please check your connection and try again.');
      });
  }, []);

  if (success) return <SuccessScreen itemName={itemName} />;

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-10 text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-6">
            <i className="ri-error-warning-fill text-red-600 text-4xl" />
          </div>
          <h2 className="font-display text-2xl font-bold text-neutral-800 mb-3">Something went wrong</h2>
          <p className="text-neutral-600 text-sm mb-8">{error}</p>
          <Link
            to="/packages"
            className="px-8 py-3 bg-amber-700 text-white font-bold rounded-full hover:bg-amber-800 transition-all whitespace-nowrap cursor-pointer inline-block"
          >
            ← Back to Packages
          </Link>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-5xl text-amber-700 animate-spin mb-4 block" />
          <p className="text-neutral-600 font-semibold">Preparing your secure checkout...</p>
          <p className="text-neutral-400 text-sm mt-2">This only takes a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <div className="relative h-52 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/e952f7cea811d5dafa229a75e047786c.jpg"
          alt="Secure Checkout"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-white" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-display text-4xl font-bold text-white drop-shadow-lg">Secure Checkout</h1>
          <p className="text-white/90 text-sm mt-2">Complete your purchase safely and securely</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#b45309',
                colorBackground: '#ffffff',
                colorText: '#1f2937',
                colorDanger: '#dc2626',
                fontFamily: 'system-ui, sans-serif',
                borderRadius: '8px',
              },
            },
          }}
        >
          <PaymentForm
            itemName={itemName}
            price={price}
            type={type}
            onSuccess={() => setSuccess(true)}
          />
        </Elements>
      </div>
    </div>
  );
}
